import React from 'react';
import { Alert, Button, Empty, Spin, Tag, Typography } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { userMessageForError } from '../../utils/apiError';
import { ADMIN_API, normaliseCoverage } from '../../utils/regulatoryAdminModel';

const { Text } = Typography;

/**
 * Coverage: domain > topic tree with the number of PUBLISHED regulatory items per topic, straight from the server.
 * A topic with none is stated neutrally ("No published requirement yet"); no obligation list is ever invented.
 */
const RegulatoryCoverage = () => {
    const { request } = useApiRequest();
    const res = useResource(async () => normaliseCoverage((await request('GET', ADMIN_API.coverage)).data), [request]);

    if (res.status === 'loading' && !res.data) return <Spin />;
    if (res.status === 'error') return <Alert type="error" message={userMessageForError(res.error)} action={<Button onClick={res.reload}>Retry</Button>} />;
    const { domains, totals, note, version } = res.data;
    if (domains.length === 0) return <Empty description="No topic taxonomy is available." />;
    return (
        <div data-testid="coverage-tree">
            {totals && (
                <div style={{ marginBottom: 12 }} data-testid="coverage-totals">
                    <Text strong>{totals.publishedItems} published item{totals.publishedItems === 1 ? '' : 's'}</Text>
                    <Text type="secondary"> · {totals.publishedUnclassified ?? 0} not yet classified · {totals.topicsWithoutPublishedRequirement ?? 0} of {totals.topics ?? '?'} topics have no published requirement yet{version ? ` · taxonomy v${version}` : ''}</Text>
                </div>
            )}
            {note && <div style={{ marginBottom: 12 }}><Text type="secondary" data-testid="coverage-note">{note}</Text></div>}
            {domains.map((d) => (
                <div key={d.id} style={{ marginBottom: 16 }} data-testid="coverage-domain">
                    <Text strong>{d.title}</Text>{' '}
                    <Text type="secondary">{d.publishedCount} published{d.uncoveredCount > 0 ? `, ${d.uncoveredCount} topic${d.uncoveredCount === 1 ? '' : 's'} without a published requirement yet` : ''}</Text>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                        {d.topics.map((t) => (
                            <li key={t.id} data-testid="coverage-topic">
                                {t.title}{' '}
                                {t.noPublishedRequirementYet ? <Tag>No published requirement yet</Tag> : <Tag color="green">{t.publishedCount} published</Tag>}
                                {t.awaitingReviewCount > 0 && <Text type="secondary">{t.awaitingReviewCount} awaiting review</Text>}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
};

export default RegulatoryCoverage;
